import { Injectable, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import * as Types from '../shared/types';
import { formatOptions } from '../shared/appUtil';
import * as Constants from '../shared/constant';
import { RoutePersistenceService } from './route-persistence.service';

@Injectable({
  providedIn: 'root',
})
export class SharedDataService {
  constructor(private routePersistenceService: RoutePersistenceService) {
    this._conversationOverview$ = new BehaviorSubject({} as Types.Overview);
    this._dateRange$ = new BehaviorSubject<Types.DateSelection>({
      startAPIDate: '',
      endAPIDate: '',
      dateSelected: false,
    });
    this._selectedColumns$ = new BehaviorSubject(['']);
    this._columnNames$ = new BehaviorSubject([] as Types.ColumnHeaders[]);
    this._totalCountSubject = new BehaviorSubject<number>(0);
    this._searchTermSubject = new BehaviorSubject<string>('');
    this._filterOnly$ = new BehaviorSubject<boolean>(false);
    this._dropDownToggleValues$ = new BehaviorSubject(Constants.InitialToggleValues as Types.DropDownToggleValues);

    this.loadDashboardStateFromSession();
  }

  private _conversationOverview$: BehaviorSubject<Types.Overview>;
  private _dateRange$: BehaviorSubject<Types.DateSelection>;
  private _selectedColumns$: BehaviorSubject<string[]>;
  private _columnNames$: BehaviorSubject<Types.ColumnHeaders[]>;
  private _dropDownToggleValues$: BehaviorSubject<Types.DropDownToggleValues>;
  private _dashboardState$: BehaviorSubject<Types.DashboardState> = new BehaviorSubject<Types.DashboardState>({
    ...Constants.EmptyDashboardState,
  });
  private _totalCountSubject = new BehaviorSubject<number>(0);
  private _searchTermSubject = new BehaviorSubject<string>('');
  private _filterOnly$ = new BehaviorSubject<boolean>(false);
  private _dashboardDateReloaded = signal<boolean>(false);
  private _apiLoading$ = new BehaviorSubject<boolean>(true);
  private _tableViewFlag = signal<boolean>(false);
  private _timezoneChange$ = new BehaviorSubject<string>('EST');

  public apiData: any;
  public apiTotalRow: any;
  selectedTimezoneValue: string = 'EST';
  // tableViewFlag: boolean = false;
  dateRangeDateObject: Types.DateObject = {} as Types.DateObject;
  hiddenColumns: string[] = [];
  // hiddenColumnsKey = 'hidden_columns';
  columnsFilterGroup: Types.FilterGroup = {
    name: 'ColumnsVisible',
    showName: 'Select Saved Filter',
    filters: {
      allSelected: false,
      tempAllSelected: false,
      options: formatOptions([]),
    },
  };

  totalConversations: any;

  public get dashboardDateReloaded(): WritableSignal<boolean> {
    return this._dashboardDateReloaded;
  }

  setDashboardDateReloaded(enabled: boolean): void {
    this._dashboardDateReloaded.set(enabled);
  }

  setDashboardState(state: Types.DashboardState) {
    this._dashboardState$.next(state);
  }

  getDashboardState(): Observable<Types.DashboardState> {
    return this._dashboardState$.asObservable();
  }

  getDashboardStateValue(): Types.DashboardState {
    return this._dashboardState$.value;
  }

  updateDashboardState(key: string, value: any): void {
    switch (key) {
      case 'tableViewFlag':
        this._dashboardState$.value.tableViewFlag = value;
        break;

      case 'filters':
        this._dashboardState$.value.filters = value;
        break;

      case 'dateRange':
        this._dashboardState$.value.dateRange = value;
        break;

      case 'navBarData':
        this._dashboardState$.value.navBarData = value;
        // Save the route when navigation data is updated
        if (value && value.routeLink) {
          // The route will be automatically saved by RoutePersistenceService
          // when the actual navigation occurs
        }
        break;

      default:
        break;
    }

    this.saveDashboardStateToSession();
  }

  /** Persist dashboard state to sessionStorage (Insights Dashboard – cleared when tab/window closes). */
  saveDashboardStateToSession(): void {
    try {
      sessionStorage.setItem('dashboardState', JSON.stringify(this._dashboardState$.value));
    } catch (e) {
      console.warn('Could not save dashboard state to sessionStorage', e);
    }
  }

  /** Load dashboard state from sessionStorage (Insights Dashboard). */
  loadDashboardStateFromSession(): void {
    try {
      const stored = sessionStorage.getItem('dashboardState');
      if (stored) {
        const dashboardState = JSON.parse(stored);
        if (dashboardState.tableViewFlag !== undefined && dashboardState.tableViewFlag !== null) {
          this.setDashboardState(dashboardState);
        }
      }
    } catch (e) {
      console.warn('Could not load dashboard state from sessionStorage', e);
    }
  }
  public get tableViewFlagStatus(): WritableSignal<boolean> {
    return this._tableViewFlag;
  }

  setTableViewFlagStatus(enabled: boolean): void {
    this._tableViewFlag.set(enabled);
    this.updateDashboardState('tableViewFlag', enabled);
  }

  updateFilterOptionsBasedOnSelection() {
    let selectedColumns: string[] = [];
    this.columnsFilterGroup.filters.options.forEach((option) => {
      option.isSelected = !this.hiddenColumns.includes(option.name);
      option.tempIsSelected = option.isSelected;
      if (!this.hiddenColumns.includes(option.name)) {
        selectedColumns.push(option.name);
      }
    });
    this._selectedColumns$.next(selectedColumns);
  }

  handlePopulateFilteredData = () => {
    let tempHiddenColumns: string[] = [];
    let tempSelectedColumns: string[] = [];
    this.columnsFilterGroup.filters.options.forEach((option) => {
      if (option.isSelected) {
        tempSelectedColumns.push(option.name);
      } else {
        tempHiddenColumns.push(option.name);
      }
    });

    // Store locally, batch update after processing
    this.hiddenColumns = tempHiddenColumns;

    // Batch update of selected columns
    this._selectedColumns$.next(tempSelectedColumns);
  };

  handleHideAll = () => {};

  handleShowAll = () => {
    this.hiddenColumns = [];
    this._selectedColumns$.next(this._columnNames$.value.map((header) => header.name));
  };

  setSelectedColumns() {
    this.hiddenColumns = [];
    let selectedColumns = this._columnNames$.value.filter((header) => header.visibility).map((header) => header.name);

    const currentSelectedColumns = this._selectedColumns$.value;

    // Only update if current selections are empty or if current selections are no longer valid options
    if (currentSelectedColumns.length === 0 || !this.areCurrentSelectionsValid(currentSelectedColumns, selectedColumns)) {
      // console.log('Updating selected columns to:', selectedColumns);
      this._selectedColumns$.next(selectedColumns);
    } else {
      // console.log('Preserving existing selected columns');
    }
  }

  forceUpdateSelectedColumns() {
    this.hiddenColumns = [];
    let selectedColumns = this._columnNames$.value.filter((header) => header.visibility).map((header) => header.name);
    this._selectedColumns$.next(selectedColumns);
  }

  // Helper method to check if selected columns match the available columns
  private haveSameColumns(current: string[], available: string[]): boolean {
    if (current.length !== available.length) return false;
    const currentSet = new Set(current);
    const availableSet = new Set(available);
    if (currentSet.size !== availableSet.size) return false;
    for (const col of currentSet) {
      if (!availableSet.has(col)) return false;
    }
    return true;
  }

  // Helper method to check if current selections are still valid options
  private areCurrentSelectionsValid(current: string[], available: string[]): boolean {
    const availableSet = new Set(available);
    // Check if all current selections are still available options
    for (const col of current) {
      if (!availableSet.has(col)) {
        return false;
      }
    }
    return true;
  }

  // Method to sync filter group options with current selections
  private syncFilterGroupWithOptions() {
    const currentSelectedColumns = this._selectedColumns$.value;

    // Update filter group options to match current selections
    this.columnsFilterGroup.filters.options.forEach((option) => {
      option.isSelected = currentSelectedColumns.includes(option.name);
      option.tempIsSelected = currentSelectedColumns.includes(option.name);
    });
  }

  /** Sync filter group options from the actual selected columns (table source of truth). Call when opening Manage Columns. */
  syncFilterGroupFromSelectedColumns() {
    if (!this.columnsFilterGroup?.filters?.options?.length) return;
    const selectedNames = (this._selectedColumns$.value ?? []).filter((name) => name != null && String(name).trim() !== '');
    this.columnsFilterGroup.filters.options.forEach((option) => {
      option.isSelected = selectedNames.includes(option.name);
      option.tempIsSelected = selectedNames.includes(option.name);
    });
  }

  setColumnNames = (columnNames: Types.ColumnHeaders[]) => {
    console.log('setColumnNames called with:', columnNames);
    this.columnsFilterGroup.filters.options = formatOptions(columnNames);
    this._columnNames$.next(columnNames);

    const availableNames = new Set(columnNames.map((h) => h.name));
    const raw = (this._selectedColumns$.value ?? []).filter((name) => name != null && String(name).trim() !== '');
    const cleanedSelected = raw.filter((name) => availableNames.has(name));
    if (cleanedSelected.length !== raw.length) {
      this._selectedColumns$.next(cleanedSelected);
    }

    // Only sync selected/visible columns if we don't already have valid user selections
    // Treat empty-string-only initial state as "no selection" so we apply API visibility
    const currentSelectedColumns = (this._selectedColumns$.value ?? []).filter(
      (name) => name != null && String(name).trim() !== ''
    );
    console.log('Current selected columns before setColumnNames sync:', currentSelectedColumns);

    if (currentSelectedColumns.length === 0) {
      this.setSelectedColumns();
    }
    this.syncFilterGroupWithOptions();
  };

  getColumnNames = () => {
    return this._columnNames$;
  };

  get totalCount$(): Observable<number> {
    return this._totalCountSubject.asObservable();
  }

  setSearchTerm(searchText: string) {
    this._searchTermSubject.next(searchText);
  }

  getSearchTerm$(): Observable<string> {
    return this._searchTermSubject.asObservable();
  }

  setTotalCount(count: number) {
    this._totalCountSubject.next(count);
  }
  updateTotalCount(count: number) {
    this._totalCountSubject.next(count);
  }

  getColumnNamesValue = () => {
    return this._columnNames$.value;
  };

  getSelectedColumns = () => {
    return this._selectedColumns$;
  };

  getColumnsFilterGroup = () => {
    return this.columnsFilterGroup;
  };

  setDateRange(data: Types.DateSelection) {
    const currentDateRangeValue = this._dateRange$.value;

    if((currentDateRangeValue.startAPIDate === data.startAPIDate) && (currentDateRangeValue.endAPIDate === data.endAPIDate)){
      return
    }
      this._dateRange$.next(data);
  }

  getDateRange() {
    return this._dateRange$.asObservable();
  }

  setDateSelected() {
    this._dateRange$.next({ ...this._dateRange$.value, dateSelected: false });
  }

  setConversationOverview(data: Types.Overview) {
    this._conversationOverview$.next(data);
  }

  getConversationOverview() {
    return this._conversationOverview$.asObservable();
  }

  setApiData(data: any): void {
    this.apiData = data;
  }

  getApiData(): any {
    return this.apiData;
  }

  setApiTotalRow(totalRow: any): void {
    this.apiTotalRow = totalRow;
  }

  getApiTotalRow(): any {
    return this.apiTotalRow;
  }

  getToggleValues() {
    return this._dropDownToggleValues$;
  }

  toggleHide(value: boolean) {
    this._dropDownToggleValues$.next({
      ...Constants.InitialToggleValues,
      hideFields: value,
    });
  }

  toggleFilter(value: boolean) {
    this._dropDownToggleValues$.next({
      ...Constants.InitialToggleValues,
      filter: value,
    });
    this.setFilterOnly(value);
  }

  toggleDate(value: boolean) {
    this._dropDownToggleValues$.next({
      ...Constants.InitialToggleValues,
      date: value,
    });
  }

  toggleMore(value: boolean) {
    this._dropDownToggleValues$.next({
      ...Constants.InitialToggleValues,
      moreOptions: value,
    });
  }

  toggleSearch(value: boolean) {
    this._dropDownToggleValues$.next({
      ...Constants.InitialToggleValues,
      search: value,
    });
  }

  toggleTimeZone(value: boolean) {
    this._dropDownToggleValues$.next({
      ...Constants.InitialToggleValues,
      timeZone: value,
    });
  }

  public filterOnly(): BehaviorSubject<boolean> {
    return this._filterOnly$;
  }

  setFilterOnly(value: boolean): void {
    if (!value) {
      this.setDashboardDateReloaded(true);
    }
    this._filterOnly$.next(value);
  }

  public apiLoading(): BehaviorSubject<boolean> {
    return this._apiLoading$;
  }

  setApiLoading(value: boolean): void {
    this._apiLoading$.next(value);
  }

  setSelectedTimezoneValue(value: string) {
    let updatedRangeObject = this.dateRangeDateObject;
    this.selectedTimezoneValue = value;

    // Emit timezone change event
    this._timezoneChange$.next(value);
  }

  getTimezoneChange(): Observable<string> {
    return this._timezoneChange$.asObservable();
  }

  setDateRangeObject(value: Types.DateObject) {
    this.dateRangeDateObject = value;
  }
}
